// GCSESubjectHub — public, indexable SEO landing page for each core GCSE subject.
// Renders an audience-led hero, "what's covered" topic strip, examiner tip
// preview pulled from examinerBank.js, parent-trust strip, FAQ accordion +
// JSON-LD FAQ/Breadcrumb schema, and a strong free-trial CTA.
//
// Three thin route wrappers (GCSEMathsRevisionHub / English / Science) pass
// the right config — keeping all the SEO copy + structure in one place.

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useState, useMemo } from 'react';
import {
  CheckCircle, Trophy, Lightning, Sparkle, Brain, ArrowRight,
  CaretDown, Target, ShieldCheck, Lightbulb, GraduationCap,
} from '@phosphor-icons/react';

import SEO from '../components/SEO';
import { mathsSections } from '../data/mathsContent';
import { englishSections } from '../data/englishContent';
import { biologySections, chemistrySections, physicsSections } from '../data/scienceContent';
import { EXAMINER_BANK } from '../data/examinerBank';

const SITE_URL = 'https://grade9ready.com';

// ---------- helpers ----------
function topicTotalsFor(subjectKey) {
  if (subjectKey === 'maths') {
    const totalTopics = mathsSections.reduce((acc, s) => acc + (s.topics?.length || 0), 0);
    const sections = mathsSections.length;
    return { totalTopics, sections };
  }
  if (subjectKey === 'english') {
    const totalTopics = englishSections.reduce((acc, s) => acc + (s.topics?.length || 0), 0);
    const sections = englishSections.length;
    return { totalTopics, sections };
  }
  // science
  const lists = [biologySections, chemistrySections, physicsSections];
  let totalTopics = 0;
  let sections = 0;
  lists.forEach((list) => {
    sections += list.length;
    list.forEach((sec) => {
      totalTopics += (sec.topics || []).length;
    });
  });
  return { totalTopics, sections };
}

function pickExaminerTipKeys(subjectKey) {
  // pick 3 representative bank keys per subject for the "Examiner tip preview"
  if (subjectKey === 'maths') return ['2.10', '2.11', '6.7'];
  if (subjectKey === 'english') return ['eng-8-1', 'eng-9-1', 'eng-13-4'];
  // science — keys match topic ids in scienceContent.js
  return ['gcse-b3-5', 'gcse-c1-7', 'gcse-p6-2'];
}

function ExaminerTipCard({ entry, label }) {
  if (!entry) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="neo-card p-5 bg-fuchsia-50 dark:bg-fuchsia-900/10 border-fuchsia-200"
      data-testid={`examiner-tip-${label}`}
    >
      <div className="text-[10px] font-black uppercase tracking-wide text-fuchsia-700 dark:text-fuchsia-300 mb-1.5 flex items-center gap-1.5">
        <Trophy size={12} weight="fill" /> Examiner tip
      </div>
      <p className="font-heading font-bold text-sm leading-snug mb-2">{label}</p>
      <p className="text-sm leading-relaxed">{entry.gradeNineMove}</p>
    </motion.div>
  );
}

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div
      className="neo-card p-0 overflow-hidden"
      data-testid={`faq-item-${idx}`}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 p-5 text-left"
      >
        <span className="font-heading font-bold text-base">{q}</span>
        <CaretDown
          size={18}
          weight="bold"
          className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed border-t border-foreground/10 pt-4">
          {a}
        </div>
      )}
    </div>
  );
}

// ---------- main component ----------
export default function GCSESubjectHub({ config }) {
  const totals = useMemo(() => topicTotalsFor(config.subjectKey), [config.subjectKey]);
  const tipKeys = useMemo(() => pickExaminerTipKeys(config.subjectKey), [config.subjectKey]);

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Subjects', item: `${SITE_URL}/subjects` },
      { '@type': 'ListItem', position: 3, name: config.crumbLabel, item: `${SITE_URL}${config.path}` },
    ],
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: config.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const courseJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: config.title,
    description: config.description,
    provider: { '@type': 'EducationalOrganization', name: 'Grade9Ready', sameAs: SITE_URL },
    educationalLevel: 'GCSE (Year 10-11)',
    inLanguage: 'en-GB',
  };

  return (
    <div className="min-h-screen bg-background" data-testid={`gcse-hub-${config.subjectKey}`}>
      <SEO
        title={config.seoTitle}
        description={config.seoDescription}
        path={config.path}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(courseJsonLd) }} />

      {/* Breadcrumbs (visible) */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 text-xs text-muted-foreground" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <span className="mx-2">/</span>
        <Link to="/subjects" className="hover:text-foreground">Subjects</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground font-bold">{config.crumbLabel}</span>
      </nav>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-10 items-center">
          <div>
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${config.heroChip} text-[11px] font-black uppercase tracking-wide mb-5`}>
              <Lightning size={12} weight="fill" /> {config.eyebrow}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black leading-[1.05] mb-5" data-testid="hub-h1">
              {config.h1}
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6 max-w-2xl">
              {config.heroLede}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/signup"
                data-testid="hub-cta-trial"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-primary text-primary-foreground font-heading font-bold shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))] hover:translate-y-[1px] transition-transform"
              >
                Create Parent Account <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/pricing"
                data-testid="hub-cta-pricing"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl border-2 border-foreground/15 font-heading font-bold hover:border-foreground/40 transition-colors"
              >
                See pricing
              </Link>
            </div>
            <p className="text-xs text-muted-foreground mt-4">No card required · cancel anytime · UK-aligned AQA content</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="neo-card p-5">
              <div className="text-[10px] font-black uppercase tracking-wide text-muted-foreground mb-1">Topics</div>
              <div className="font-heading font-black text-3xl" data-testid="hub-stat-topics">{totals.totalTopics}+</div>
              <div className="text-xs text-muted-foreground mt-1">live across {totals.sections} sections</div>
            </div>
            <div className="neo-card p-5">
              <div className="text-[10px] font-black uppercase tracking-wide text-muted-foreground mb-1">Marking</div>
              <div className="font-heading font-black text-3xl">AI</div>
              <div className="text-xs text-muted-foreground mt-1">instant WWW + EBI feedback</div>
            </div>
            <div className="neo-card p-5">
              <div className="text-[10px] font-black uppercase tracking-wide text-muted-foreground mb-1">Tier</div>
              <div className="font-heading font-black text-3xl">F · H</div>
              <div className="text-xs text-muted-foreground mt-1">Foundation + Higher</div>
            </div>
            <div className="neo-card p-5">
              <div className="text-[10px] font-black uppercase tracking-wide text-muted-foreground mb-1">Exam board</div>
              <div className="font-heading font-black text-3xl">AQA</div>
              <div className="text-xs text-muted-foreground mt-1">spec-aligned</div>
            </div>
          </div>
        </div>
      </section>

      {/* What's covered */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t-2 border-foreground/10">
        <h2 className="text-3xl sm:text-4xl font-heading font-black mb-4">What&rsquo;s covered</h2>
        <p className="text-muted-foreground max-w-3xl mb-10">{config.coverageLede}</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="hub-coverage-grid">
          {config.coverage.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04 }}
              className="neo-card p-5"
              data-testid={`hub-coverage-${i}`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${config.heroChip} mb-3`}>
                <Target size={18} weight="bold" />
              </div>
              <h3 className="font-heading font-bold text-base mb-1.5">{c.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Examiner tip preview */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t-2 border-foreground/10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h2 className="text-3xl sm:text-4xl font-heading font-black mb-2">Top examiner tips</h2>
            <p className="text-muted-foreground max-w-2xl">
              A taste of the AQA-aligned <strong>Examiner brain</strong> sitting on every GCSE topic page. Inside the platform, every topic gets four blocks: why marks are lost, what examiners want, common traps, and a Grade-9 move.
            </p>
          </div>
          <Link
            to="/signup"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:underline"
            data-testid="hub-cta-examiner-trial"
          >
            See all tips inside <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {tipKeys.map((k, i) => (
            <ExaminerTipCard key={k} entry={EXAMINER_BANK[k]} label={config.tipLabels[i] || `Tip ${i + 1}`} />
          ))}
        </div>
      </section>

      {/* Why parents choose us */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t-2 border-foreground/10">
        <h2 className="text-3xl sm:text-4xl font-heading font-black mb-8">Why families choose Grade9Ready</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Brain,        title: 'AI-marked answers',         body: 'Every long-form response gets WWW + EBI feedback in seconds — no waiting for marking.' },
            { icon: Target,       title: 'Adaptive practice',         body: 'Difficulty scales 9 levels so pupils stay in the productive struggle zone.' },
            { icon: ShieldCheck,  title: 'Parent visibility',         body: 'Real-time progress, GCSE compliance score, weekly digest emails.' },
            { icon: GraduationCap,title: 'Built for Grade 9',         body: 'Examiner tips, mistake intelligence, mock-exam mode all wired into the journey.' },
          ].map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="neo-card p-5" data-testid={`hub-trust-${i}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-primary/10 text-primary mb-3`}>
                  <Icon size={18} weight="bold" />
                </div>
                <h3 className="font-heading font-bold text-base mb-1.5">{p.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t-2 border-foreground/10">
        <h2 className="text-3xl sm:text-4xl font-heading font-black mb-8 text-center">Frequently asked</h2>
        <div className="space-y-3" data-testid="hub-faq">
          {config.faqs.map((f, i) => (
            <FAQItem key={f.q} q={f.q} a={f.a} idx={i} />
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="neo-card p-8 sm:p-12 text-center bg-gradient-to-br from-primary/5 to-primary/10 border-primary/30">
          <Sparkle size={28} weight="fill" className="mx-auto mb-3 text-primary" />
          <h2 className="text-3xl sm:text-4xl font-heading font-black mb-3">{config.finalCtaH2}</h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-6">{config.finalCtaLede}</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/signup"
              data-testid="hub-cta-final-trial"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-primary text-primary-foreground font-heading font-bold shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))] hover:translate-y-[1px] transition-transform"
            >
              Create Parent Account <ArrowRight size={18} weight="bold" />
            </Link>
            <Link
              to={config.appHubPath}
              data-testid="hub-cta-final-explore"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl border-2 border-foreground/15 font-heading font-bold hover:border-foreground/40 transition-colors"
            >
              <Lightbulb size={16} weight="bold" /> Explore the platform
            </Link>
          </div>
          <ul className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-6 text-xs text-muted-foreground">
            <li className="inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" className="text-emerald-500" /> No card required</li>
            <li className="inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" className="text-emerald-500" /> Cancel anytime</li>
            <li className="inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" className="text-emerald-500" /> AQA-aligned</li>
          </ul>
        </div>
      </section>
    </div>
  );
}
